import Handsontable from 'handsontable';

import {
  openMerchandisePopup,
  type MerchandisePopupNavigationOptions,
} from '../materials/merchandise-calculates/merchandise-calculate';

import type {
  ExtendedContextMenuSettings,
  HandsontableWithClickTime,
  PlanTableData,
  RowsWithErrors,
  SelectionPayload,
  SelectionRange,
} from './handsontable.types';

export type { RowsWithErrors } from './handsontable.types';

import { EPlanFormulationProductTypeLabel } from '@/features/plans/types/plan.enum';

/**
 * Check if current click is a double-click
 */
export const isDoubleClick = (hot: Handsontable): boolean => {
  const now = Date.now();
  const hotWithClick = hot as HandsontableWithClickTime;
  const lastClick = hotWithClick.__lastClickTime || 0;
  const timeSinceLastClick = now - lastClick;
  hotWithClick.__lastClickTime = now;
  return timeSinceLastClick < 300;
};

/**
 * Open popup on a cell with double-click detection
 */
export const openCellPopup = (
  hot: Handsontable,
  coords: { row: number; col: number },
  popupFn: (hot: Handsontable, row: number, col: number, cell: HTMLElement) => void
): void => {
  hot.selectCell(coords.row, coords.col);
  const cellElement = hot.getCell(coords.row, coords.col) as HTMLElement;
  if (cellElement) {
    popupFn(hot, coords.row, coords.col, cellElement);
  }
};

/** 1-line table: column index of 商品・種類 (formulation product type). */
export const ONE_LINE_FORMULATION_PRODUCT_TYPE_COL = 10;
/** 3-line table (line 1): column index of 商品・種類. */
export const THREE_LINE_FORMULATION_PRODUCT_TYPE_COL = 11;

/**
 * Whether the merchandise spec popup may open for 内容 in this row.
 * Only「生コン」uses the popup;「他商品」uses plain cell editing (value is preserved when switching type).
 */
export const isMerchandisePopupAllowed = (
  hot: Handsontable,
  row: number,
  formulationProductTypeCol: number
): boolean => {
  const productTypeValue = hot.getDataAtCell(row, formulationProductTypeCol);
  return productTypeValue === EPlanFormulationProductTypeLabel.CONCRETE;
};

/**
 * Handle merchandise popup (double-click on 内容). Opens only when 種類 is 生コン.
 * @param formulationProductTypeCol - Same row: 種類 column (10 for 1-line, 11 for 3-line line 1).
 */
export const handleMerchandiseColumn = (
  event: MouseEvent,
  hot: Handsontable,
  coords: { row: number; col: number },
  formulationProductTypeCol: number,
  merchandiseNavigation?: MerchandisePopupNavigationOptions
): void => {
  if (!isDoubleClick(hot)) return;

  if (!isMerchandisePopupAllowed(hot, coords.row, formulationProductTypeCol)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const nav: MerchandisePopupNavigationOptions = merchandiseNavigation ?? {
    contentCol: coords.col,
    formulationProductTypeCol,
    getNextMerchandiseRow: (r: number) => r + 1,
    isSelectableRow: undefined,
  };

  // Use requestAnimationFrame to ensure DOM is ready
  globalThis.requestAnimationFrame(() => {
    globalThis.requestAnimationFrame(() => {
      openCellPopup(hot, coords, (h, r, c, cell) => openMerchandisePopup(h, r, c, cell, nav));
    });
  });
};

export interface TryOpenMerchandisePopupOnEnterOptions {
  contentCol: number;
  formulationProductTypeCol: number;
  /** When set, Enter opens the popup only for rows where this returns true (e.g. 3-line line 1). */
  isSelectableRow?: (row: number) => boolean;
  /** Next row for 内容 after Enter-dismiss without save (3-line: usually current + rowsPerPlan). */
  getNextMerchandiseRow?: (row: number) => number;
}

/**
 * On Enter, open the merchandise spec popup for 内容 when 種類 is 生コン (same rules as double-click).
 * @returns true if the key event was consumed; return false from Handsontable `beforeKeyDown` when true.
 */
export const tryOpenMerchandisePopupOnEnter = (
  hot: Handsontable,
  event: KeyboardEvent,
  options: TryOpenMerchandisePopupOnEnterOptions
): boolean => {
  if (event.key !== 'Enter') {
    return false;
  }

  const activeEditor = hot.getActiveEditor?.();
  if (activeEditor?.isOpened?.()) {
    return false;
  }

  const selected = hot.getSelected?.();
  if (!selected?.length) {
    return false;
  }

  const [r1, c1, r2, c2] = selected[0] as number[];
  if (r1 !== r2 || c1 !== c2) {
    return false;
  }

  if (options.isSelectableRow && !options.isSelectableRow(r1)) {
    return false;
  }

  if (c1 !== options.contentCol) {
    return false;
  }

  if (!isMerchandisePopupAllowed(hot, r1, options.formulationProductTypeCol)) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();

  const nav: MerchandisePopupNavigationOptions = {
    contentCol: options.contentCol,
    formulationProductTypeCol: options.formulationProductTypeCol,
    getNextMerchandiseRow: options.getNextMerchandiseRow ?? ((r: number) => r + 1),
    isSelectableRow: options.isSelectableRow,
  };

  globalThis.requestAnimationFrame(() => {
    globalThis.requestAnimationFrame(() => {
      openCellPopup(hot, { row: r1, col: c1 }, (h, r, c, cell) =>
        openMerchandisePopup(h, r, c, cell, nav)
      );
    });
  });

  return true;
};

/**
 * Parameters for handling layout switch from 3-line to 1-line
 */
export interface HandleSwitchToSingleLineParams {
  /** Whether currently in 3-line layout */
  isThreeLineLayout: boolean;
  /** Ref containing getTableData function from Handsontable */
  getTableDataRef: { current: (() => PlanTableData) | null };
  /** Current 3-line table data from state */
  tableData3Line: PlanTableData;
  /** Callback to handle 3-line table data changes */
  handleTableDataChange3Line: (data: PlanTableData) => void;
  /** Callback to set layout state */
  setIsThreeLineLayout: (value: boolean) => void;
}

/**
 * Parameters for handling layout switch from 1-line to 3-line
 */
export interface HandleSwitchToThreeLineParams {
  /** Whether currently in 3-line layout */
  isThreeLineLayout: boolean;
  /** Ref containing getTableData function from Handsontable */
  getTableDataRef: { current: (() => PlanTableData) | null };
  /** Callback to handle 1-line table data changes */
  handleTableDataChange: (data: PlanTableData) => void;
  /** Callback to set layout state */
  setIsThreeLineLayout: (value: boolean) => void;
}

/**
 * Type guard to check if value is a non-empty array
 */
const isNonEmptyArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.length > 0;

/**
 * Check if data is valid 3-line format (rows divisible by 3)
 */
const isValid3LineData = (data: unknown[]): boolean => data.length % 3 === 0;

/**
 * Handle invalid 3-line data with fallback
 * Returns true if data was synced, false if only switched without sync
 */
const handleInvalid3LineData = (
  currentData: unknown[],
  fallbackData: unknown[] | null,
  syncAndSwitch: (data: unknown[]) => void,
  switchWithoutSync: () => void
): boolean => {
  if (fallbackData) {
    console.warn(
      `[handsontable-helpers] Invalid 3-line data from table: ${currentData.length} rows, using state data: ${fallbackData.length} rows`
    );
    syncAndSwitch(fallbackData);
    return true; // Data was synced
  }

  console.warn(
    `[handsontable-helpers] Invalid 3-line data: ${currentData.length} rows (not divisible by 3), skipping sync`
  );
  switchWithoutSync();
  return false; // Only switched without sync
};

/**
 * Handle error during data sync with fallback
 */
const handleSyncError = (
  error: unknown,
  fallbackData: unknown[] | null,
  syncAndSwitch: (data: unknown[]) => void
): boolean => {
  console.warn('[handsontable-helpers] Error syncing data before switch to 1-line:', error);

  if (fallbackData) {
    syncAndSwitch(fallbackData);
    return true;
  }

  return false;
};

/**
 * Process table data from getTableData function
 * Returns true if action was taken (synced or switched), false if no action was taken
 */
const processTableData = (
  getTableData: () => PlanTableData,
  fallbackData: unknown[] | null,
  syncAndSwitch: (data: unknown[]) => void,
  switchWithoutSync: () => void
): boolean => {
  try {
    const currentData = getTableData();

    if (!isNonEmptyArray(currentData)) {
      return false;
    }

    if (!isValid3LineData(currentData)) {
      // handleInvalid3LineData handles both sync and switch internally
      // Returns true if synced, false if only switched
      handleInvalid3LineData(currentData, fallbackData, syncAndSwitch, switchWithoutSync);
      return true; // Action was taken (either synced or switched)
    }

    // Current data is valid 3-line format
    syncAndSwitch(currentData);
    return true;
  } catch (error) {
    const wasSynced = handleSyncError(error, fallbackData, syncAndSwitch);
    if (!wasSynced) {
      // Error occurred and no fallback, need to switch without sync
      switchWithoutSync();
    }
    return true; // Action was taken (either synced or switched)
  }
};

/**
 * Handle switching from 3-line layout to 1-line layout
 * Syncs data from Handsontable before switching to ensure data consistency
 *
 * @param params - Parameters for handling the layout switch
 * @returns onClick handler function
 */
export const createSwitchToSingleLineHandler = (
  params: HandleSwitchToSingleLineParams
): (() => void) => {
  const {
    isThreeLineLayout,
    getTableDataRef,
    tableData3Line,
    handleTableDataChange3Line,
    setIsThreeLineLayout,
  } = params;

  return () => {
    // Early return if not in 3-line layout (already in 1-line, no action needed)
    if (!isThreeLineLayout) {
      return;
    }

    const getTableData = getTableDataRef.current;
    const fallbackData = isNonEmptyArray(tableData3Line) ? tableData3Line : null;

    // Helper function to sync data and switch layout
    const syncAndSwitchToOneLine = (data: unknown[]) => {
      handleTableDataChange3Line(data as PlanTableData);
      // Ensure state update completes before switching layout
      setTimeout(() => {
        setIsThreeLineLayout(false);
      }, 0);
    };

    // Helper function to switch layout without syncing
    const switchToOneLineWithoutSync = () => {
      setIsThreeLineLayout(false);
    };

    // Priority: get data directly from table
    if (getTableData) {
      const processed = processTableData(
        getTableData,
        fallbackData,
        syncAndSwitchToOneLine,
        switchToOneLineWithoutSync
      );
      if (processed) {
        return;
      }
    } else if (fallbackData) {
      // No getTableData available, use state data if available
      syncAndSwitchToOneLine(fallbackData);
      return;
    }

    // Final case: cannot sync anything but still allow switch
    switchToOneLineWithoutSync();
  };
};

/**
 * Handle switching from 1-line layout to 3-line layout
 * Syncs data from Handsontable before switching to ensure data consistency
 *
 * @param params - Parameters for handling the layout switch
 * @returns onClick handler function
 */
export const createSwitchToThreeLineHandler = (
  params: HandleSwitchToThreeLineParams
): (() => void) => {
  const { isThreeLineLayout, getTableDataRef, handleTableDataChange, setIsThreeLineLayout } =
    params;

  return () => {
    // Early return if already in 3-line layout (no action needed)
    if (isThreeLineLayout) {
      setIsThreeLineLayout(true);
      return;
    }

    const getTableData = getTableDataRef.current;

    // Helper function to sync data and switch layout
    const syncAndSwitchToThreeLine = (data: unknown[]) => {
      handleTableDataChange(data as PlanTableData);
      // Ensure state update completes before switching layout
      setTimeout(() => {
        setIsThreeLineLayout(true);
      }, 0);
    };

    // Helper function to switch layout without syncing
    const switchToThreeLineWithoutSync = () => {
      setIsThreeLineLayout(true);
    };

    // Priority: get data directly from table
    if (getTableData) {
      try {
        const currentData = getTableData();

        if (isNonEmptyArray(currentData)) {
          // Current data is in 1-line format, sync and switch
          syncAndSwitchToThreeLine(currentData);
          return;
        }
      } catch (error) {
        console.warn('[handsontable-helpers] Error syncing data before switch to 3-line:', error);
      }
    }

    // Final case: cannot sync but still allow switch
    switchToThreeLineWithoutSync();
  };
};

/**
 * Adjust rowsWithErrors indices after inserting a row
 * When inserting at insertIndex, all errors with rowIndex >= insertIndex should be shifted +1
 *
 * @param rowsWithErrors - Current validation errors array
 * @param insertIndex - Index where row was inserted
 * @returns Updated validation errors array with adjusted indices
 */
export const adjustRowsWithErrorsAfterInsert = (
  rowsWithErrors: RowsWithErrors,
  insertIndex: number
): RowsWithErrors => {
  return rowsWithErrors.map((error) => {
    if (error.rowIndex >= insertIndex) {
      return {
        ...error,
        rowIndex: error.rowIndex + 1,
      };
    }
    return error;
  });
};

/**
 * Adjust rowsWithErrors indices after deleting a row
 * When deleting at deleteIndex, all errors with rowIndex > deleteIndex should be shifted -1
 * Errors with rowIndex === deleteIndex should be removed
 *
 * @param rowsWithErrors - Current validation errors array
 * @param deleteIndex - Index where row was deleted
 * @returns Updated validation errors array with adjusted indices
 */
export const adjustRowsWithErrorsAfterDelete = (
  rowsWithErrors: RowsWithErrors,
  deleteIndex: number
): RowsWithErrors => {
  return rowsWithErrors
    .filter((error) => error.rowIndex !== deleteIndex)
    .map((error) => {
      if (error.rowIndex > deleteIndex) {
        return {
          ...error,
          rowIndex: error.rowIndex - 1,
        };
      }
      return error;
    });
};

/**
 * Create context menu settings for Handsontable
 * Common function used by both 1-line and 3-line formats
 */
export interface CreateContextMenuSettingsParams {
  onAddRowAbove?: (index: number) => void;
  onAddRowBelow?: (index: number) => void;
  onDeleteRows?: (indices: number[]) => void;
  readOnly?: boolean;
  isConfirmView?: boolean;
  allowSTTColumn?: boolean; // For 3-line format: allow context menu on STT column (col === 0)
  getRowIndexForDelete?: (selection: SelectionPayload) => number; // Transform row index for delete (e.g., for 3-line format)
  getRowIndexForAddAbove?: (selection: SelectionPayload) => number; // Transform row index for add above (e.g., for 3-line format)
  getRowIndexForAddBelow?: (selection: SelectionPayload) => number; // Transform row index for add below (e.g., for 3-line format)
}

export const createContextMenuSettings = (
  params: CreateContextMenuSettingsParams
): false | ExtendedContextMenuSettings => {
  const {
    onAddRowAbove,
    onAddRowBelow,
    onDeleteRows,
    readOnly = false,
    isConfirmView = false,
    allowSTTColumn = false,
    getRowIndexForDelete,
    getRowIndexForAddAbove,
    getRowIndexForAddBelow,
  } = params;

  // Disable context menu completely for read-only or confirm tables
  if (readOnly || isConfirmView) {
    return false;
  }

  // Helper function to get default row index from selection
  const getDefaultRowIndex = (selection: SelectionPayload): number => {
    const firstRange = selection[0];

    if (Array.isArray(firstRange)) {
      return typeof firstRange[0] === 'number' ? firstRange[0] : 0;
    }

    return typeof firstRange?.start?.row === 'number' ? firstRange.start.row : 0;
  };

  const getRowsFromSelectionRange = (range: SelectionRange | null | undefined): number[] => {
    if (!range) return [];

    const createRowIndicesArray = (startRow: number, endRow: number): number[] => {
      const minRow = Math.min(startRow, endRow);
      const maxRow = Math.max(startRow, endRow);
      return Array.from({ length: maxRow - minRow + 1 }, (_, i) => minRow + i);
    };

    // Handle tuple format: [startRow, startCol, endRow, endCol]
    if (Array.isArray(range) && range.length >= 4) {
      const startRow = range[0];
      const endRow = range[2];
      if (typeof startRow === 'number' && typeof endRow === 'number') {
        return createRowIndicesArray(startRow, endRow);
      }
    }

    // Handle object format: {start: {row, col}, end: {row, col}}
    if (!Array.isArray(range) && range?.start?.row != null && range?.end?.row != null) {
      const startRow = range.start.row;
      const endRow = range.end.row;
      if (typeof startRow === 'number' && typeof endRow === 'number') {
        return createRowIndicesArray(startRow, endRow);
      }
    }

    return [];
  };

  const getSelectedRowsDescending = (selection: SelectionPayload): number[] => {
    if (!Array.isArray(selection) || selection.length === 0) {
      return [];
    }

    const uniqueRows = new Set<number>(
      selection.flatMap((range) => getRowsFromSelectionRange(range))
    );

    return Array.from(uniqueRows).sort((a, b) => b - a);
  };

  // Create wrapper functions that include default logic to avoid duplication
  const getRowIndexForAddAboveWithDefault = getRowIndexForAddAbove ?? getDefaultRowIndex;
  const getRowIndexForAddBelowWithDefault = getRowIndexForAddBelow ?? getDefaultRowIndex;
  const getRowIndexForDeleteWithDefault = getRowIndexForDelete ?? getDefaultRowIndex;

  const settings: ExtendedContextMenuSettings = {
    beforeContextMenuShow: function (_menu: unknown, coords: { row: number; col: number }) {
      // Show context menu for row headers (col === -1)
      if (coords.col === -1) {
        return true;
      }
      // For 3-line format: also show for STT column (col === 0)
      if (allowSTTColumn && coords.col === 0) {
        return true;
      }
      return false;
    },
    items: {
      row_above: {
        name: '上に行を追加',
        callback: function (_key: string, selection: SelectionPayload) {
          // Use custom transform function if provided (e.g., for 3-line format)
          const rowIndex = getRowIndexForAddAboveWithDefault(selection);
          if (onAddRowAbove) {
            onAddRowAbove(rowIndex);
          }
        },
      },
      row_below: {
        name: '下に行を追加',
        callback: function (_key: string, selection: SelectionPayload) {
          // Use custom transform function if provided (e.g., for 3-line format)
          const rowIndex = getRowIndexForAddBelowWithDefault(selection);
          if (onAddRowBelow) {
            onAddRowBelow(rowIndex);
          }
        },
      },
      '---------': {},
      remove_row: {
        name: '行を削除',
        callback: function (this: Handsontable, _key: string, selection: SelectionPayload) {
          if (!onDeleteRows) return;

          const liveSelection =
            this != null && typeof this.getSelected === 'function'
              ? (this.getSelected() as SelectionPayload)
              : selection;
          const selectedRows = getSelectedRowsDescending(liveSelection);

          // Fallback for unexpected selection payloads.
          if (selectedRows.length === 0) {
            const fallbackIndex = getRowIndexForDeleteWithDefault(liveSelection);
            onDeleteRows([fallbackIndex]);
            return;
          }

          const transformedRows = Array.from(
            new Set(
              selectedRows.map((row) =>
                getRowIndexForDeleteWithDefault([
                  {
                    start: { row },
                    end: { row },
                  },
                ])
              )
            )
          ).sort((a, b) => b - a);

          onDeleteRows(transformedRows);
        },
      },
    },
  };

  return settings;
};
